import { useState, useRef } from 'react'
import { useChatStore } from '@/store/chatStore'
import { useChat } from '@/hooks/useChat'
import { useSignalR } from '@/hooks/useSignalR'
import { useAppointmentBooking } from '@/hooks/useAppointmentBooking'
import { emailTranscript } from '@/services/api'
import { ChatHeader } from './ChatHeader'
import { ChatMessages } from './ChatMessages'
import { ChatInput } from './ChatInput'
import { ChatHistorySidebar } from './ChatHistorySidebar'
import type { AvailableSlot } from '@/types'

interface Props {
  onClose?: () => void
}

export function ChatPanel({ onClose }: Props) {
  const config            = useChatStore((s) => s.config)
  const warningMessage    = useChatStore((s) => s.warningMessage)
  const currentSessionId  = useChatStore((s) => s.currentSessionId)
  const messages          = useChatStore((s) => s.messages)
  const authToken         = useChatStore((s) => s.authToken)
  const addUserMessage    = useChatStore((s) => s.addUserMessage)
  const addAssistantPlaceholder = useChatStore((s) => s.addAssistantPlaceholder)
  const finalizeMessage   = useChatStore((s) => s.finalizeMessage)

  const { connection, isConnected } = useSignalR(config?.apiUrl || '', authToken)
  const { sendChat } = useChat({ connection, isConnected })

  const {
    bookingStep,
    availableSlots,
    stepPrompt,
    handleTextInput,
    handleSlotSelect,
    cancelBooking,
  } = useAppointmentBooking()

  // Email transcript state
  const [showEmailBar, setShowEmailBar] = useState(false)
  const [emailValue, setEmailValue]     = useState('')
  const [emailSending, setEmailSending] = useState(false)
  const [emailDone, setEmailDone]       = useState(false)

  // Booking text input state
  const [bookingInput, setBookingInput] = useState('')
  const [bookingError, setBookingError] = useState<string | null>(null)
  const [bookingLoading, setBookingLoading] = useState(false)
  const bookingInputRef = useRef<HTMLInputElement>(null)

  async function handleEmailTranscript() {
    if (!currentSessionId || !emailValue.trim()) return
    setEmailSending(true)
    try {
      await emailTranscript(currentSessionId, emailValue.trim())
      setEmailDone(true)
      setEmailValue('')
      setTimeout(() => { setShowEmailBar(false); setEmailDone(false) }, 2000)
    } catch {
      // silent
    } finally {
      setEmailSending(false)
    }
  }

  async function handleBookingSubmit() {
    if (!bookingInput.trim() || bookingLoading) return
    setBookingLoading(true)
    setBookingError(null)
    const err = await handleTextInput(bookingInput.trim())
    setBookingLoading(false)
    if (err) {
      setBookingError(err)
    } else {
      setBookingInput('')
      bookingInputRef.current?.focus()
    }
  }

  async function onSlotSelect(slot: AvailableSlot) {
    setBookingLoading(true)
    setBookingError(null)
    const result = await handleSlotSelect(slot)
    setBookingLoading(false)
    if (result.success) {
      // Append a success confirmation message to the chat
      addUserMessage(`📅 Appointment booked for ${slot.dayLabel} at ${slot.timeLabel}`)
      const aiMsg   = addAssistantPlaceholder()
      // Finalize immediately with a static confirmation — no LLM call needed
      setTimeout(() => {
        finalizeMessage(aiMsg.id)
        // Replace placeholder content directly via store
        useChatStore.setState((s) => ({
          messages: s.messages.map((m) =>
            m.id === aiMsg.id
              ? { ...m, content: `Your appointment has been requested for **${slot.dayLabel} at ${slot.timeLabel}**. We'll be in touch to confirm. Is there anything else I can help you with?` }
              : m
          ),
        }))
      }, 100)
    } else {
      setBookingError(result.error ?? 'Booking failed.')
    }
  }

  const canEmailTranscript = !!currentSessionId && messages.length > 0

  return (
    <div className="
      relative flex flex-col
      w-full h-full
      bg-[var(--color-panel)]
      rounded-widget shadow-widget
      overflow-hidden
    ">
      <ChatHeader onClose={onClose} />

      {warningMessage && (
        <div className="
          px-4 py-2 text-xs text-center
          bg-amber-50 text-amber-700
          border-b border-amber-200
        ">
          ⚠️ {warningMessage}
        </div>
      )}

      <div className="relative flex-1 overflow-hidden">
        <ChatMessages onSend={sendChat} isConnected={isConnected} />
        <ChatHistorySidebar />
      </div>

      {/* Email transcript bar */}
      {showEmailBar && (
        <div className="
          flex items-center gap-2 px-4 py-2
          border-t border-[var(--color-border)]
          bg-[var(--color-surface)]
        ">
          <input
            type="email"
            autoFocus
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleEmailTranscript()}
            placeholder="your@email.com"
            className="
              flex-1 text-xs bg-transparent outline-none
              text-[var(--color-text)]
              placeholder:text-[var(--color-text-sub)]
            "
          />
          {emailDone ? (
            <span className="text-xs" style={{ color: 'var(--color-brand)' }}>✓ Sent!</span>
          ) : (
            <button
              onClick={handleEmailTranscript}
              disabled={emailSending || !emailValue.trim()}
              className="
                text-xs px-3 py-1 rounded-lg
                bg-[var(--color-brand)] text-white
                disabled:opacity-40 transition-opacity
              "
            >
              {emailSending ? '...' : 'Send'}
            </button>
          )}
          <button
            onClick={() => setShowEmailBar(false)}
            className="text-[var(--color-text-sub)] text-xs px-1"
          >✕</button>
        </div>
      )}

      {/* Booking input panel — replaces ChatInput while booking is active */}
      {bookingStep !== null ? (
        <div className="
          border-t border-[var(--color-border)]
          bg-[var(--color-panel)]
          px-4 py-3
        ">
          <p className="text-xs text-[var(--color-text-sub)] mb-2">{stepPrompt}</p>

          {/* Slot chips for 'slot' step */}
          {bookingStep === 'slot' && (
            <div className="max-h-44 overflow-y-auto flex flex-col gap-1 mb-2">
              {availableSlots.length === 0 ? (
                <p className="text-xs text-[var(--color-text-sub)] italic">
                  No available slots in the next 7 days.
                </p>
              ) : (
                (() => {
                  // Group slots by dayLabel
                  const byDay: Record<string, AvailableSlot[]> = {}
                  for (const s of availableSlots) {
                    if (!byDay[s.dayLabel]) byDay[s.dayLabel] = []
                    byDay[s.dayLabel].push(s)
                  }
                  return Object.entries(byDay).map(([day, slots]) => (
                    <div key={day}>
                      <p className="text-[10px] font-semibold text-[var(--color-text-sub)] mb-1 mt-1">{day}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {slots.map((slot) => (
                          <button
                            key={`${slot.date}-${slot.slotStart}`}
                            onClick={() => onSlotSelect(slot)}
                            disabled={bookingLoading}
                            className="
                              text-[11px] px-2.5 py-1 rounded-lg
                              border border-[var(--color-border)]
                              bg-[var(--color-surface)]
                              text-[var(--color-text)]
                              hover:border-[var(--color-brand)]
                              hover:text-[var(--color-brand)]
                              transition-colors disabled:opacity-40
                            "
                          >
                            {slot.timeLabel}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                })()
              )}
            </div>
          )}

          {/* Text input for name/whatsapp/email steps */}
          {bookingStep !== 'slot' && (
            <div className="flex gap-2 items-center">
              <input
                ref={bookingInputRef}
                autoFocus
                type={bookingStep === 'email' ? 'email' : bookingStep === 'whatsapp' ? 'tel' : 'text'}
                value={bookingInput}
                onChange={(e) => { setBookingInput(e.target.value); setBookingError(null) }}
                onKeyDown={(e) => e.key === 'Enter' && handleBookingSubmit()}
                placeholder={
                  bookingStep === 'name'     ? 'Your full name…'
                  : bookingStep === 'whatsapp' ? '+971501234567'
                  : 'you@example.com'
                }
                className="
                  flex-1 text-sm bg-transparent outline-none
                  border border-[var(--color-border)] rounded-lg
                  px-3 py-2
                  text-[var(--color-text)]
                  placeholder:text-[var(--color-text-sub)]
                  focus:border-[var(--color-brand)]
                "
                disabled={bookingLoading}
              />
              <button
                onClick={handleBookingSubmit}
                disabled={!bookingInput.trim() || bookingLoading}
                className="
                  w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                  bg-[var(--color-brand)] text-white
                  disabled:opacity-40 transition-opacity
                "
              >
                {bookingLoading ? (
                  <span className="text-xs">…</span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
                )}
              </button>
            </div>
          )}

          {bookingError && (
            <p className="text-[11px] text-red-500 mt-1">{bookingError}</p>
          )}

          <button
            onClick={cancelBooking}
            className="
              mt-2 text-[11px] text-[var(--color-text-sub)]
              hover:text-red-500 transition-colors
            "
          >
            Cancel booking
          </button>
        </div>
      ) : (
        <ChatInput
          onSend={sendChat}
          isConnected={isConnected}
          canEmailTranscript={canEmailTranscript}
          onEmailTranscript={() => setShowEmailBar((v) => !v)}
        />
      )}
    </div>
  )
}
